import '@fontsource/ibm-plex-sans-arabic/arabic-400.css';
import '@fontsource/ibm-plex-sans-arabic/arabic-500.css';
import '@fontsource/ibm-plex-sans-arabic/arabic-600.css';
import '@fontsource/ibm-plex-sans-arabic/latin-400.css';
import '@fontsource/ibm-plex-sans-arabic/latin-500.css';
import '@fontsource/ibm-plex-sans-arabic/latin-600.css';
import '@fontsource/reem-kufi/arabic-600.css';
import '@fontsource/reem-kufi/latin-600.css';
import './styles.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, type ReactNode, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { PageLoading } from '@/components/states';
import { DirectionProvider } from '@/components/ui/direction';
import { Toaster } from '@/components/ui/sonner';
import { ApiError } from '@/lib/api';
import { I18nProvider, useI18n } from '@/lib/i18n';

// Staff screens are a separate chunk: drivers (S3) never download them.
const StaffApp = lazy(() => import('./staff/StaffApp'));
// Drivers' pages (/s/<token>, /login, /me…): their own chunk, Arabic only.
const DriverApp = lazy(() => import('./driver/DriverApp'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // a 4xx won't fix itself by retrying
      retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
    },
  },
});
// a session that ended while the page was open → back to the login screen
queryClient.getQueryCache().subscribe((ev) => {
  const err = ev.query.state.error;
  if (ev.type === 'updated' && err instanceof ApiError && err.code === 'NOT_LOGGED_IN' && ev.query.queryKey[0] !== 'me')
    queryClient.setQueryData(['me'], null);
});

const router = createBrowserRouter([
  {
    path: '/admin/*',
    element: (
      <Suspense fallback={<PageLoading />}>
        <StaffApp />
      </Suspense>
    ),
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<PageLoading />}>
        <DriverApp />
      </Suspense>
    ),
  },
]);

/** Radix components (menus, selects, switches…) follow the page language: RTL for Arabic, LTR for English. */
function Directional({ children }: { children: ReactNode }) {
  const { lang } = useI18n();
  return <DirectionProvider dir={lang === 'ar' ? 'rtl' : 'ltr'}>{children}</DirectionProvider>;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <Directional>
          <RouterProvider router={router} />
          <Toaster position="top-center" />
        </Directional>
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>,
);
